import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { previewArtworkPalette, representativeArtworkColor } from '../utils/previewArtworkPalette';
import './PreviewArtworkBackground.css';

const artworkColors = new Map();

// Sampling is separate from the displayed image: a cover without CORS support
// can still be shown, with a theme-aware tint providing safe text contrast.
const getArtworkColor = (url) => {
  if (artworkColors.has(url)) return artworkColors.get(url);
  const color = new Promise(resolve => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = 24;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        context.drawImage(image, 0, 0, 24, 24);
        const pixels = context.getImageData(0, 0, 24, 24).data;
        resolve(representativeArtworkColor(pixels));
      } catch {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = url;
  });
  if (artworkColors.size >= 64) artworkColors.delete(artworkColors.keys().next().value);
  artworkColors.set(url, color);
  return color;
};

const PreviewArtworkBackground = ({ backgroundImage, onBackgroundError, className = '', children }) => {
  const darkMode = useTheme().palette.mode === 'dark';
  const [artworkColor, setArtworkColor] = useState(null);
  const [loadedBackground, setLoadedBackground] = useState(null);
  const representativeColor = artworkColor && artworkColor.url === backgroundImage ? artworkColor.color : null;
  const palette = previewArtworkPalette(representativeColor, darkMode);

  useEffect(() => {
    if (!backgroundImage || loadedBackground !== backgroundImage) return undefined;
    let current = true;
    getArtworkColor(backgroundImage).then(value => {
      if (current) setArtworkColor({ url: backgroundImage, color: value });
    });
    return () => { current = false; };
  }, [backgroundImage, loadedBackground]);

  return (
    <span
      className={['previewArtworkBackground', className].filter(Boolean).join(' ')}
      data-tone={darkMode ? 'dark' : 'light'}
      style={{
        '--fallback-background': palette.background.join(', '),
        '--fallback-ink': `rgb(${palette.ink.join(', ')})`,
        '--fallback-mark': palette.mark.join(', '),
      }}>
      {backgroundImage && <img className='previewArtworkWash' src={backgroundImage} alt='' loading='lazy' onLoad={() => setLoadedBackground(backgroundImage)} onError={onBackgroundError} />}
      <span className='previewArtworkShade' aria-hidden='true' />
      {children}
    </span>
  );
};

export default PreviewArtworkBackground;
